/**
 * @file       Admin vuex store, used by the whole admin module.
 * @author     Appwapp <info@appwapp.com>
 * @copyright  © Centre collégial de développement de matériel didactique, Collège de Maisonneuve, 2021
 * @license    https://www.ccdmd.qc.ca
 * @version    1.0
 */

import axios from "@/plugins/axios.js";

/**
 * Gets the default state.
 *
 * @return  {Object}  The default state.
 */
function getDefaultState() {
  return {
    users: {
      status: null,
      data: { data: [] },
      error: null,
      filters: {
        role: "teacher",
        status: null,
        id: null,
        name: null,
        group: null,
        resourcesPerPage: 10,
        page: 1
      }
    },
    user: {
      status: null,
      data: null,
      error: null
    },
    sendInvitation: {
      status: null,
      data: null,
      error: null
    },
    notifications: {
      status: null,
      data: { data: [] },
      error: null,
      filters: {
        type: null,
        searche: null,
        resourcesPerPage: 10,
        page: 1
      }
    },
    notification: {
      status: null,
      data: null,
      error: null
    },
    totalGrades: {
      status: null,
      data: { data: [] },
      error: null,
      filters: {
        status: null,
        id: null,
        name: null,
        group: null,
        resourcesPerPage: 10,
        page: 1
      }
    },
    gradeDeliberation: {
      status: null,
      data: null,
      error: null
    }
  };
}

/**
 * Admin vuex actions.
 *
 * @type  {Object}
 */
const actions = {

  /**
   * Create user
   *
   * @param   {Function}  arg1.commit      Commit the action
   * @param   {Object}    payload          The payload
   *
   * @return  {Promise}
   */
  createUser: ({ commit }, payload) => {
    return new Promise((resolve, reject) => {
      commit("onCreateUser");

      axios
        .post(`admin/register`, payload)
        .then(response => {
          commit("onCreateUserSuccess", response);
          resolve(response);
        })
        .catch(error => {
          commit("onCreateUserError", error);
          reject(error);
        });
    });
  },

  /**
   * Get users
   *
   * @param   {Function}  arg1.commit      Commit the action
   * @param   {Object}    arg1.state       The vuex state
   *
   * @return  {Promise}
   */
  getUsers: ({ commit, state }) => {
    return new Promise((resolve, reject) => {
      const payload = Object.entries(state.users.filters).reduce(
        (a, [k, v]) => (v === null || v === "" ? a : { ...a, [k]: v }),
        {}
      );
      commit("onGetUsers");
      axios
        .get(`user`, { params: payload })
        .then(response => {
          commit("onGetUsersSuccess", response);
          resolve(response);
        })
        .catch(error => {
          commit("onGetUsersError", error);
          reject(error);
        });
    });
  },

  /**
   * Get memberships
   *
   * @param   {Function}  arg1.commit      Commit the action
   * @param   {Object}    arg1.state       The vuex state
   *
   * @return  {Promise}
   */
  getMemberships: ({ commit, state }) => {
    return new Promise((resolve, reject) => {
      const payload = Object.entries(state.users.filters).reduce(
        (a, [k, v]) => (v === null || v === "" ? a : { ...a, [k]: v }),
        {}
      );
      commit("onGetUsers");
      axios
        .get(`teacher/group/filter`, { params: payload })
        .then(response => {
          commit("onGetUsersSuccess", response);
          resolve(response);
        })
        .catch(error => {
          commit("onGetUsersError", error);
          reject(error);
        });
    });
  },

  /**
   * Put memberships.
   *
   * @param   {Function}  arg1.dispatch  Dispatch the action
   * @param   {Function}  arg1.commit    Commit the action
   * @param   {Object}    params         The parameters
   *
   * @return  {Promise}
   */
  putMemberships: ({ commit, dispatch }, params) => {
    const url = params.all ? "teacher/group/statusAll" : "teacher/group/status";
    return new Promise((resolve, reject) => {
      commit("onPutUser");
      axios
        .put(url, params)
        .then(response => {
          commit("onPutUserSuccess", response);
          dispatch("getMemberships");
          resolve(response);
        })
        .catch(error => {
          commit("onPutUserError", error);
          reject(error);
        });
    });
  },

  /**
   * Get user by ID
   *
   * @param   {Function}  arg1.commit      Commit the action
   * @param   {Object}    userID           The user's ID
   *
   * @return  {Promise}
   */
  getUserByID: ({ commit }, userID) => {
    return new Promise((resolve, reject) => {
      commit("onGetUserByID");
      axios
        .get(`user`, { params: { id: userID } })
        .then(response => {
          commit("onGetUserByIDSuccess", response);
          resolve(response);
        })
        .catch(error => {
          commit("onGetUserByIDError", error);
          reject(error);
        });
    });
  },

  /**
   * Put the user.
   *
   * @param   {Function}  arg1.commit  Commit the action
   * @param   {Object}    params       The parameters
   *
   * @return  {Promise}
   */
  putUser: ({ commit }, params) => {
    return new Promise((resolve, reject) => {
      commit("onPutUser");
      axios
        .put(`user`, params)
        .then(response => {
          commit("onPutUserSuccess", response);
          resolve(response);
        })
        .catch(error => {
          commit("onPutUserError", error);
          reject(error);
        });
    });
  },

  /**
   * Patch the user.
   *
   * @param   {Function}  arg1.dispatch  Dispatch the action
   * @param   {Function}  arg1.commit    Commit the action
   * @param   {Object}    params         The parameters
   *
   * @return  {Promise}
   */
  patchUser: ({ dispatch, commit }, params) => {
    return new Promise((resolve, reject) => {
      commit("onPatchUser");
      axios
        .patch(`user`, params)
        .then(response => {
          commit("onPatchUserSuccess", response);
          dispatch("getUsers");
          resolve(response);
        })
        .catch(error => {
          commit("onPatchUserError", error);
          reject(error);
        });
    });
  },

  /**
   * Post to send the invitation email
   *
   * @param   {Function}  arg1.commit  Commit the action
   * @param   {Object}    params       The parameters
   *
   * @return  {Promise}
   */
  postSendInvitation: ({ commit }, params) => {
    return new Promise((resolve, reject) => {
      commit("onPostSendInvitation");
      axios
        .post(`user/sendInvitation`, params)
        .then(response => {
          commit("onPostSendInvitationSuccess", response);
          resolve(response);
        })
        .catch(error => {
          commit("onPostSendInvitationError", error);
          reject(error);
        });
    });
  },

  /**
   * Get notifications
   *
   * @param   {Function}  arg1.commit      Commit the action
   * @param   {Object}    arg1.state       The vuex state
   *
   * @return  {Promise}
   */
  getNotifications: ({ commit, state }) => {
    return new Promise((resolve, reject) => {
      const payload = Object.entries(state.notifications.filters).reduce(
        (a, [k, v]) => (v === null || v === "" ? a : { ...a, [k]: v }),
        {}
      );
      commit("onGetNotifications");
      axios
        .get(`user/notification`, { params: payload })
        .then(response => {
          commit("onGetNotificationsSuccess", response);
          resolve(response);
        })
        .catch(error => {
          commit("onGetNotificationsError", error);
          reject(error);
        });
    });
  },

  /**
   * Put notification.
   *
   * @param   {Function}  arg1.commit    Commit the action
   * @param   {Function}  arg1.dispatch  Dispatch the action
   * @param   {Object}    params         The parameters
   *
   * @return  {Promise}
   */
  putNotification: ({ commit, dispatch }, params) => {
    return new Promise((resolve, reject) => {
      commit("onPutNotification");
      axios
        .put(`user/notification`, params)
        .then(response => {
          commit("onPutNotificationSuccess", response);
          dispatch("getNotifications");
          resolve(response);
        })
        .catch(error => {
          commit("onPutNotificationError", error);
          reject(error);
        });
    });
  },

  /**
   * Get total grades
   *
   * @param   {Function}  arg1.commit      Commit the action
   * @param   {Object}    arg1.state       The vuex state
   *
   * @return  {Promise}
   */
  getTotalGrades: ({ commit, state }) => {
    return new Promise((resolve, reject) => {
      const payload = Object.entries(state.totalGrades.filters).reduce(
        (a, [k, v]) => (v === null || v === "" ? a : { ...a, [k]: v }),
        {}
      );
      commit("onGetTotalGrades");
      axios
        .get(`teacher/grade/totalGrades`, { params: payload })
        .then(response => {
          commit("onGetTotalGradesSuccess", response);
          resolve(response);
        })
        .catch(error => {
          commit("onGetTotalGradesError", error);
          reject(error);
        });
    });
  },

  /**
   * Post grade deliberation.
   *
   * @param   {Function}  arg1.commit    Commit the action
   * @param   {Function}  arg1.dispatch  Dispatch the action
   * @param   {Object}    params         The parameters
   *
   * @return  {Promise}
   */
  postGradeDeliberation: ({ commit, dispatch }, params) => {
    return new Promise((resolve, reject) => {
      commit("onPostGradeDeliberation");
      axios
        .post(`teacher/grade/gradeDeliberation`, params)
        .then(response => {
          commit("onPostGradeDeliberationSuccess", response);
          dispatch("getTotalGrades");
          resolve(response);
        })
        .catch(error => {
          commit("onPostGradeDeliberationError", error);
          reject(error);
        });
    });
  }
};

/**
 * Admin vuex mutations.
 *
 * @type  {Object}
 */
const mutations = {
  /**
   * Create user.
   *
   * @param  {Object}  state   The vuex state
   */
  onCreateUser: state => {
    state.user.status = "fetching";
    state.user.error = {};
  },

  /**
   * Create user success.
   *
   * @param  {Object}  state        The vuex state
   * @param  {Object}  arg2.data    The data
   */
  onCreateUserSuccess: (state, { data }) => {
    state.user.status = "success";
    state.user.error = null;
    state.user.data = data;
  },

  /**
   * Create user error.
   *
   * @param  {Object}  state   The vuex state
   * @param  {Object}  err     The error
   */
  onCreateUserError: (state, err) => {
    state.user.status = "error";
    state.user.error = err;
  },

  /**
   * Get users.
   *
   * @param  {Object}  state   The vuex state
   */
  onGetUsers: state => {
    state.users.status = "fetching";
    state.users.error = {};
  },

  /**
   * Get users success.
   *
   * @param  {Object}  state        The vuex state
   * @param  {Object}  arg2.data    The data
   */
  onGetUsersSuccess: (state, { data }) => {
    state.users.status = "success";
    state.users.error = null;
    state.users.data = data;
  },

  /**
   * Get users error.
   *
   * @param  {Object}  state   The vuex state
   * @param  {Object}  err     The error
   */
  onGetUsersError: (state, err) => {
    state.users.status = "error";
    state.users.error = err;
  },

  /**
   * Get user by ID.
   *
   * @param  {Object}  state   The vuex state
   */
  onGetUserByID: state => {
    state.user.status = "fetching";
    state.user.error = {};
  },

  /**
   * Get user by ID success.
   *
   * @param  {Object}  state        The vuex state
   * @param  {Object}  arg2.data    The data
   */
  onGetUserByIDSuccess: (state, { data }) => {
    state.user.status = "success";
    state.user.error = null;
    state.user.data = data.data.find(u => u);
  },

  /**
   * Get user by ID error.
   *
   * @param  {Object}  state   The vuex state
   * @param  {Object}  err     The error
   */
  onGetUserByIDError: (state, err) => {
    state.user.status = "error";
    state.user.error = err;
  },

  /**
   * Set user filters.
   *
   * @param  {Object}  state       The vuex state
   * @param  {Object}  filters     The filters
   */
  setUserFilters: (state, filters) => {
    if (filters.reset) {
      state.users.filters = {
        role: null,
        status: null,
        id: null,
        name: null,
        group: null,
        resourcesPerPage: 10,
        page: 1
      };
    }
    if (filters.fields) {
      state.users.filters = {
        ...state.users.filters,
        ...filters.fields
      };
    }
  },

  /**
   * Set the user.
   *
   * @param  {Object}  state    The vuex state
   * @param  {Object}  user     The user
   */
  setUser: (state, user) => {
    state.user.data = user;
  },

  /**
   * Put user.
   *
   * @param  {Object}  state   The vuex state
   */
  onPutUser: state => {
    state.user.status = "fetching";
    state.user.error = {};
  },

  /**
   * Put user success.
   *
   * @param  {Object}  state        The vuex state
   * @param  {Object}  arg2.data    The data
   */
  onPutUserSuccess: (state, { data }) => {
    state.user.status = "success";
    state.user.error = null;
    state.user.data = data.data;
  },

  /**
   * Put user error.
   *
   * @param  {Object}  state   The vuex state
   * @param  {Object}  err     The error
   */
  onPutUserError: (state, err) => {
    state.user.status = "error";
    state.user.error = err;
  },

  /**
   * Patch user.
   *
   * @param  {Object}  state   The vuex state
   */
  onPatchUser: state => {
    state.user.status = "fetching";
    state.user.error = {};
  },

  /**
   * Patch user success.
   *
   * @param  {Object}  state        The vuex state
   * @param  {Object}  arg2.data    The data
   */
  onPatchUserSuccess: (state, { data }) => {
    state.user.status = "success";
    state.user.error = null;
    state.user.data = data.data;
  },

  /**
   * Patch user error.
   *
   * @param  {Object}  state   The vuex state
   * @param  {Object}  err     The error
   */
  onPatchUserError: (state, err) => {
    state.user.status = "error";
    state.user.error = err;
  },

  /**
   * Post user invitation.
   *
   * @param  {Object}  state   The vuex state
   */
  onPostSendInvitation: state => {
    state.sendInvitation.status = "fetching";
    state.sendInvitation.error = {};
  },

  /**
   * Post user invitation success.
   *
   * @param  {Object}  state        The vuex state
   * @param  {Object}  arg2.data    The data
   */
  onPostSendInvitationSuccess: (state, { data }) => {
    state.sendInvitation.status = "success";
    state.sendInvitation.error = null;
    state.sendInvitation.data = data;
  },

  /**
   * Post user invitation error.
   *
   * @param  {Object}  state   The vuex state
   * @param  {Object}  err     The error
   */
  onPostSendInvitationError: (state, err) => {
    state.sendInvitation.status = "error";
    state.sendInvitation.error = err;
  },

  /**
   * Get notifications.
   *
   * @param  {Object}  state   The vuex state
   */
  onGetNotifications: state => {
    state.notifications.status = "fetching";
    state.notifications.error = {};
  },

  /**
   * Get notifications success.
   *
   * @param  {Object}  state        The vuex state
   * @param  {Object}  arg2.data    The data
   */
  onGetNotificationsSuccess: (state, { data }) => {
    state.notifications.status = "success";
    state.notifications.error = null;
    state.notifications.data = data;
  },

  /**
   * Get notifications error.
   *
   * @param  {Object}  state   The vuex state
   * @param  {Object}  err     The error
   */
  onGetNotificationsError: (state, err) => {
    state.notifications.status = "error";
    state.notifications.error = err;
  },

  /**
   * Set notification filters.
   *
   * @param  {Object}  state       The vuex state
   * @param  {Object}  filters     The filters
   */
  setNotificationFilters: (state, filters) => {
    if (filters.reset) {
      state.notifications.filters = {
        type: null,
        searche: null,
        resourcesPerPage: 10,
        page: 1
      };
    }
    if (filters.fields) {
      state.notifications.filters = {
        ...state.notifications.filters,
        ...filters.fields
      };
    }
  },

  /**
   * Put notification.
   *
   * @param  {Object}  state   The vuex state
   */
  onPutNotification: state => {
    state.notification.status = "fetching";
    state.notification.error = {};
  },

  /**
   * Put notification success.
   *
   * @param  {Object}  state        The vuex state
   * @param  {Object}  arg2.data    The data
   */
  onPutNotificationSuccess: (state, { data }) => {
    state.notification.status = "success";
    state.notification.error = null;
    state.notification.data = data;
  },

  /**
   * Put notification error.
   *
   * @param  {Object}  state   The vuex state
   * @param  {Object}  err     The error
   */
  onPutNotificationError: (state, err) => {
    state.notification.status = "error";
    state.notification.error = err;
  },

  /**
   * Get total grades.
   *
   * @param  {Object}  state   The vuex state
   */
  onGetTotalGrades: state => {
    state.totalGrades.status = "fetching";
    state.totalGrades.error = {};
  },

  /**
   * Get total grades success.
   *
   * @param  {Object}  state        The vuex state
   * @param  {Object}  arg2.data    The data
   */
  onGetTotalGradesSuccess: (state, { data }) => {
    state.totalGrades.status = "success";
    state.totalGrades.error = null;
    state.totalGrades.data = data;
  },

  /**
   * Get total grades error.
   *
   * @param  {Object}  state   The vuex state
   * @param  {Object}  err     The error
   */
  onGetTotalGradesError: (state, err) => {
    state.totalGrades.status = "error";
    state.totalGrades.error = err;
  },

  /**
   * Set total grades filters.
   *
   * @param  {Object}  state       The vuex state
   * @param  {Object}  filters     The filters
   */
  setTotalGradesFilters: (state, filters) => {
    if (filters.reset) {
      state.totalGrades.filters = {
        status: null,
        id: null,
        name: null,
        group: null,
        resourcesPerPage: 10,
        page: 1
      };
    }
    if (filters.fields) {
      state.totalGrades.filters = {
        ...state.totalGrades.filters,
        ...filters.fields
      };
    }
  },

  /**
   * Post grade deliberation.
   *
   * @param  {Object}  state   The vuex state
   */
  onPostGradeDeliberation: state => {
    state.gradeDeliberation.status = "fetching";
    state.gradeDeliberation.error = {};
  },

  /**
   * Post grade deliberation success.
   *
   * @param  {Object}  state        The vuex state
   * @param  {Object}  arg2.data    The data
   */
  onPostGradeDeliberationSuccess: (state, { data }) => {
    state.gradeDeliberation.status = "success";
    state.gradeDeliberation.error = null;
    state.gradeDeliberation.data = data;
  },

  /**
   * Post grade deliberation error.
   *
   * @param  {Object}  state   The vuex state
   * @param  {Object}  err     The error
   */
  onPostGradeDeliberationError: (state, err) => {
    state.gradeDeliberation.status = "error";
    state.gradeDeliberation.error = err;
  }
};

/**
 * Export the store.
 *
 * @type  {Object}
 */
export default {
  namespaced: true,
  state: getDefaultState(),
  actions,
  mutations
};
